import React, { useEffect, useState, useRef, useCallback } from 'react';
import _, { debounce } from 'lodash';
import { Dimmer, Icon, Header, Popup, Button, Form, Divider, Grid } from 'semantic-ui-react';
import { useSelector, useDispatch } from 'react-redux';
import { designActions } from '../../actions';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import juiceClient from 'juice/client';
import { ReactSVG } from 'react-svg';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { generateImageLink } from '../../utils/design.utils';
import './GBHeader.css';

const GBHeader = props => {
  const { t } = useTranslation();
  const { _type, _designId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(state => state.authentication.user);
  const isOperationalUser = ['operator', 'admin'].includes(user.role);
  const templateId = useSelector(state => state.template.templateId);
  const name = useSelector(state => state.design.name);
  const pagesCount = useSelector(state => state.design.pages.length);
  const saving = useSelector(state => state.design.saving);
  const saved = useSelector(state => state.design.saved);
  const designId = useSelector(state => state.design.designId);
  const exportError = useSelector(state => state.design.exportError);
  const exportErrorStatus = _.get(exportError, 'status');
  const exportState = useSelector(state => state.design.exportState);
  const exportLink = useSelector(state => state.design.exportLink);
  const [nameDraft, setNameDraft] = useState(name || '');
  const [downloading, setDownloading] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [exportType, setExportType] = useState('png');
  const [exportPages, setExportPages] = useState([]);
  const [activeDim, setActiveDim] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const toggledSaving = useRef(false);
  const enablePrompt = useRef(false);
  const savingTimerHandler = useRef(null);
  const pageOptions = useRef([]);
  const typeOptions = [
    {
      key: 1,
      text: 'PNG',
      value: 'png',
      content: <Header content="PNG" subheader={t('highQualityImage')} />,
    },
    {
      key: 2,
      text: 'PDF',
      value: 'pdf',
      content: <Header content="PDF" subheader={t('highMultiDocument')} />,
    },
    // {
    //   key: 3,
    //   text: 'MP4',
    //   value: 'mp4',
    //   content: <Header content="MP4" />,
    // },
  ];

  const debouncedChangeName = useCallback(
    debounce(value => dispatch(designActions.changeName(value)), 300),
    [dispatch]
  );

  useEffect(() => {
    setNameDraft(name || '');
  }, [name]);

  useEffect(() => {
    return () => debouncedChangeName.cancel();
  }, [debouncedChangeName]);

  useEffect(() => {
    // callback function to call when event triggers
    const onPageLoad = () => {
      setTimeout(() => {
        setPageLoaded(true);
      }, 10000);
      // do something else
    };

    // Check if the page has already loaded
    if (document.readyState === 'complete') {
      onPageLoad();
    } else {
      window.addEventListener('load', onPageLoad, false);
      // Remove the event listener when component unmounts
      return () => window.removeEventListener('load', onPageLoad);
    }
  }, []);

    useEffect(() => {
    document.documentElement.dataset.theme = 'dark';
    if (isOperationalUser && _designId) {
      if (_type === 'template') dispatch(designActions.fetchTemplateDesign(_designId));
      else if (_type === 'text') dispatch(designActions.fetchTextDesign(_designId));
      else dispatch(designActions.fetch(_designId));
      enablePrompt.current = true;
    } else if (user.role === 'client' && !_designId) {
      history.push('/home');
    } else if (_designId && _designId !== designId) {
      dispatch(designActions.fetch(_designId));
      enablePrompt.current = true;
    }

    document.addEventListener('keydown', keydownHandler);

    return () => {
      if (savingTimerHandler.current) {
        clearTimeout(savingTimerHandler.current);
      }
      document.removeEventListener('keydown', keydownHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // console.log('Try to export 0', props.getExport);
    if (props.mode === 'export') {
      _saveDesign();
      if (props.getExport) {
        // console.log('Try to export');
        setExportPages([1, 2]);
        setExportType('png');
        exportDesign();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.getExport]);

  const exportVersion = 'v0.7';
  useEffect(() => {
    if (!exportLink) return undefined;

    let cancelled = false;
    let delayedDownload;
    const re = /(?:\.([^.]+))?$/;
    const filenameWithExtension = exportLink.substring(exportLink.lastIndexOf('/') + 1);
    const extension = re.exec(filenameWithExtension)[1];
    const authenticatedExportLink = generateImageLink(exportLink, user.authToken);
    const addCacheParameter = (parameterName, value) =>
      `${authenticatedExportLink}${authenticatedExportLink.includes('?') ? '&' : '?'}${parameterName}=${value}`;

    const downloadExport = async () => {
      try {
        const downloadUrl = addCacheParameter('download', new Date().getTime());
        const response = await fetch(downloadUrl, { credentials: 'include' });
        if (!response.ok) throw new Error(`Download failed with status ${response.status}`);

        const blob = await response.blob();
        if (cancelled) return;

        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `${name || 'Untitled design'}.${extension}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
      } catch (error) {
        if (!cancelled) toast.error(t('downloadFailed'));
      } finally {
        if (!cancelled) setDownloading(false);
      }
    };

    if (props.mode === 'export') {
      window.postMessage(
        JSON.stringify({
          link: addCacheParameter('q', new Date().getTime()),
          name: `${name || 'Untitled design'}.${extension}`,
        }),
      );
      delayedDownload = setTimeout(downloadExport, 300);
    } else {
      downloadExport();
    }

    return () => {
      cancelled = true;
      if (delayedDownload) clearTimeout(delayedDownload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportLink]);

  useEffect(() => {
    if (exportState === 'exporting') {
      setDownloading(true);
    } else if (exportState === 'failed') {
      if (exportErrorStatus === 402) {
        setActiveDim(true);
      } else {
        toast.error(t('downloadFailed'));
      }
      setDownloading(false);
    }
  }, [exportErrorStatus, exportState, t]);

  useEffect(() => {
    pageOptions.current.length = 0;
    const tempExportPages = [];
    for (let i = 0; i < pagesCount; i++) {
      pageOptions.current.push({
        key: i,
        text: `${t('page')} ${i + 1}`,
        value: i,
      });
      tempExportPages.push(i);
    }
    setExportPages(tempExportPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesCount]);

  useEffect(() => {
    let timestamp;
    let el;
    if (saved && templateId) {
      timestamp = new Date().getTime();
      el = document.getElementById(templateId);
      if (el) el.src = `${el.src.split('?')[0]}?t=${timestamp}`;
    } else if (saved && _type === 'text') {
      timestamp = new Date().getTime();
      el = document.getElementById(_designId);
      if (el) el.src = `${el.src.split('?')[0]}?t=${timestamp}`;
    } else if (_type === 'design' && !saved && designId && !saving && !toggledSaving.current) {
      toggledSaving.current = true;
      _saveDesign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, saving]);

  const gotoHome = () => {
    if (isOperationalUser) window.close();
    history.push('/home');
  };

  const _saveDesign = () => {
    if (savingTimerHandler.current) clearTimeout(savingTimerHandler.current);
    savingTimerHandler.current = setTimeout(() => {
      savingTimerHandler.current = null;
      _exportAllStyles().then(styles => {
        _inlineStyle(styles).then(previewPages => {
          dispatch(designActions.save(previewPages));
          toggledSaving.current = false;
        });
      });
    }, 3000);
  };

  const _exportAllStyles = () => {
    return new Promise(resolve => {
      let styles = '';
      const allStyles = document.getElementsByTagName('style');
      for (let style of allStyles) {
        if (style && style.firstChild) {
          for (let node of style.childNodes) {
            styles = styles.concat(node.data);
          }
        }
      }
      resolve(styles);
    });
  };

  const _inlineStyle = styles => {
    return new Promise(resolve => {
      const artboards = document.getElementsByClassName('artboard');
      if (_.get(artboards, 'length', 0) > 0) {
        const previewPages = [];
        Array.from(artboards).forEach(artboard => {
          previewPages.push(juiceClient.inlineContent(artboard.outerHTML, styles));
        });
        resolve(previewPages);
      }
    });
  };

  const exportDesign = () => {
    setOpenPopup(false);
    if (!designId || exportPages.length === 0 || downloading) return;
    // console.log('export', exportPages, exportType);
    let styles = '';
    const allStyles = document.getElementsByTagName('style');
    for (let style of allStyles) {
      if (style && style.firstChild) {
        for (let node of style.childNodes) {
          styles = styles.concat(node.data);
        }
      }
    }
    const artboards = document.getElementsByClassName('artboard');
    // console.log('artboard', artboards);
    if (_.get(artboards, 'length', 0) > 0) {
      const pages = [];
      Array.from(artboards).forEach((artboard, index) => {
        if (exportPages.indexOf(index) !== -1) pages.push(juiceClient.inlineContent(artboard.outerHTML, styles));
      });
      // console.log('pages', pages);
      dispatch(designActions.exportDesign(designId, { pagesIndex: exportPages, pages }, exportType));
    }
  };

  const keydownHandler = event => {
    if (event.keyCode === 90 && event.ctrlKey && event.shiftKey) dispatch(designActions.redo());
    else if (event.keyCode === 90 && event.ctrlKey) dispatch(designActions.undo());
  };

  const LoaderSpinner = () => {
    return (
      <div className="loaderContainer" style={{ position: 'absolute', backgroundColor: '#fff', zIndex: 999 }}>
        <div className="lds-grid">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    )
  }
  
  const DefaultMode = () => {
    return (
      <header className="GBHeader">
        <Prompt when={enablePrompt.current} message={t('areYouSure') + (saved ? '' : '\n' + t('designHasNotSaved'))} />
        <div className="rightBox">
          <div className="logo" onClick={gotoHome}>
            <img alt="Monda" src={require('../../assets/images/vibinoLogoBlack.svg')} />
          </div>
          <div className="undoRedoBox">
            <Popup
              content={`${t('redo')} Ctrl+Shift+Z`}
              trigger={
                <div className="headerBtn undoRedoButton" onClick={() => dispatch(designActions.redo())}>
                  <Icon name="redo" />
                </div>
              }
            />
            <Popup
              content={`${t('undo')} Ctrl+Z`}
              trigger={
                <div className="headerBtn undoRedoButton" onClick={() => dispatch(designActions.undo())}>
                  <Icon name="undo" />
                </div>
              }
            />
          </div>
          <div className="saving-state">
            <span>{saved ? t('allChangesSaved') : saving ? t('saveChanges') : t('unsavedChanges')}</span>
          </div>
        </div>
        <div className="leftBox">
          <div className="designName">
            <input
              type="text"
              value={nameDraft}
              placeholder={t('untitled')}
              onChange={event => {
                setNameDraft(event.target.value);
                debouncedChangeName(event.target.value);
              }}
              onBlur={() => debouncedChangeName.flush()}
            />
          </div>
          {/* <div className="headerBtn themeMode" title="Night/Day Mode" onClick={themeChange}>
          <Icon name="angle left" />
        </div> */}

          {/* For double btn remove class name downloadBtnSingle */}
          <Button.Group className="downloadBtn downloadBtnSingle" floated="right">
            <Popup
              className="downloadPopup"
              on="click"
              basic
              onClose={() => setOpenPopup(false)}
              onOpen={() => setOpenPopup(true)}
              open={openPopup}
              trigger={
                <ReactSVG
                  title="Download"
                  src="/assets/images/icons/download.svg"
                  wrapper="span"
                  className="downloadIcon"
                />
                // <Button title="Download">
                //   <span className="labelBtn">{t('download')}</span>
                // </Button>
              }
            >
              <Popup.Content>
                <Form>
                  <Grid>
                    <Grid.Column floated="right">
                      <span className="downloadTitle">
                        <ReactSVG src="/assets/images/icons/download.svg" wrapper="span" />
                        <span className="labelBtn">{t('download')}</span>
                      </span>
                    </Grid.Column>
                    <Grid.Column className="forPad" floated="left">
                      <Icon name="delete" onClick={() => setOpenPopup(false)} />
                    </Grid.Column>
                  </Grid>
                  <Divider />
                  <Form.Select
                    className="exportDD"
                    fluid
                    options={typeOptions}
                    label={t('fileTypes')}
                    value={exportType}
                    onChange={(event, { value }) => setExportType(value)}
                  />
                  {pagesCount > 1 && (
                    <Form.Select
                      className="exportPagesDD"
                      multiple
                      selection
                      fluid
                      options={pageOptions.current}
                      label={t('choosePages')}
                      value={exportPages}
                      onChange={(event, { value }) => setExportPages(value)}
                    />
                  )}
                  <Button
                    basic
                    fluid
                    content={t('download')}
                    disabled={downloading || exportPages.length === 0}
                    onClick={exportDesign}
                  />
                </Form>
              </Popup.Content>
            </Popup>
            {/* <Button icon="angle down" /> */}
          </Button.Group>
          <div className="headerBtn back" title="Back" onClick={gotoHome}>
            <span>{t('goBack')}</span>
            <ReactSVG title="Back" src="/assets/images/icons/back.svg" wrapper="span" className="backIcon" />
            {/* <Icon name="angle left" /> */}
          </div>
        </div>
        <Dimmer active={downloading} page className="exportingDesign">
          <Header as="h2" icon inverted>
            {/* <ReactSVG title="Back" src="/assets/images/icons/spinner.svg" wrapper="span" className="downloadSpinner" /> */}
            {/* <Icon name="cloud download" /> */}
            
            <div>
              <div className="progress-bar-container">
                <div className="progress-bar stripes animated reverse slower">
                  <span className="progress-bar-inner"></span>
                </div>
              </div>
            </div>

            {t('exportingDesign')}
            <Header.Subheader>{t('pleaseWait')} </Header.Subheader>
          </Header>
        </Dimmer>
        <Dimmer active={activeDim} blurring="true" className="attentionAlert">
          <Header as="h2" icon>
            {t('noSubscription')}
            <br />
          </Header>
          <div className="dimContent">
            <Button className="vButton colorPrimary" content={t('gotIt')} onClick={() => setActiveDim(false)} />
          </div>
        </Dimmer>
      </header>
    );
  };

  const handleExportPages = pageIndex => {
    let newExportPages = exportPages;
    const index = newExportPages.indexOf(pageIndex);
    if (index > -1) {
      newExportPages.splice(index, 1);
      setExportPages([...newExportPages]);
    } else {
      setExportPages([...exportPages, pageIndex]);
    }
  };

  const ExportMode = () => {
    return (
      <header className="GBHeader exportMode">
        {!pageLoaded && <LoaderSpinner />}
        <div className="exportContent">
          <Prompt
            when={enablePrompt.current}
            message={t('areYouSure') + (saved ? '' : '\n' + t('designHasNotSaved'))}
          />
          <div className="designName">
            <input
              type="text"
              value={name || ''}
              placeholder={t('untitled')}
              onChange={event => dispatch(designActions.changeName(event.target.value))}
              disabled
            />
          </div>
          <span style={{ color: '#000', display: 'none' }}>{exportVersion}</span>
          <div className="title">{t('fileTypes')}</div>
          <div className="exportTypeBtn">
            {typeOptions.map((type, index) => {
              return (
                <Button
                  key={index}
                  className={exportType === type.value ? 'active' : null}
                  onClick={() => setExportType(type.value)}
                >
                  {type.content}
                </Button>
              );
            })}
          </div>
          {pagesCount > 1 && (
            <>
              <div className="title">{t('choosePages')}</div>
              <div className="exportTypeBtn pages">
                {pageOptions.current.map((page, index) => {
                  return (
                    <Button
                      key={index}
                      className={exportPages.includes(page.value) ? 'active' : null}
                      onClick={() => handleExportPages(page.value)}
                    >
                      {page.text}
                    </Button>
                  );
                })}
              </div>
            </>
          )}
          <div className="leftBox">
            <Form>
              <Form.Select
                className="exportDD"
                fluid
                options={typeOptions}
                label={t('fileTypes')}
                value={exportType}
                onChange={(event, { value }) => setExportType(value)}
              />
              {pagesCount > 1 && (
                <Form.Select
                  className="exportPagesDD"
                  multiple
                  selection
                  fluid
                  options={pageOptions.current}
                  label={t('choosePages')}
                  value={exportPages}
                  onChange={(event, { value }) => setExportPages(value)}
                />
              )}
              <Button
                fluid
                className="exportBtn"
                content={t('download')}
                disabled={downloading || exportPages.length === 0}
                onClick={exportDesign}
              />
            </Form>
          </div>
          <Dimmer active={downloading} page className={`exportingDesign exportMode`}>
            <Header as="h2" icon inverted>
              {/* <ReactSVG title="Back" src="/assets/images/icons/spinner.svg" wrapper="span" className="downloadSpinner" /> */}
              <div>
              <div className="progress-bar-container">
                <div className="progress-bar stripes animated reverse slower">
                  <span className="progress-bar-inner"></span>
                </div>
              </div>
            </div>
              {/* <Icon name="cloud download" /> */}
              {t('exportingDesign')}
              <Header.Subheader>{t('pleaseWait')}</Header.Subheader>
            </Header>
          </Dimmer>
          <Dimmer active={activeDim} blurring="true" className="attentionAlert">
            <Header as="h2" icon>
              {t('noSubscription')}
              <br />
            </Header>
            <div className="dimContent">
              <Button className="vButton colorPrimary" content={t('gotIt')} onClick={() => setActiveDim(false)} />
            </div>
          </Dimmer>
        </div>
      </header>
    );
  };

  if (props.mode === 'export') {
    return ExportMode();
  } else {
    return DefaultMode();
  }
};

export default GBHeader;
